import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  CreatePlanAccountRequest,
  ListPlanAccountsQuery,
  UpdatePlanAccountRequest,
} from './PlanAccounts.types';

import {
  listPlanAccounts,
  deletePlanAccount,
  createPlanAccount,
  updatePlanAccount,
  getPlanAccount,
  activateOrInactivatePlanAccount,
  lastPlanAccount,
  listPlanAccountsAll,
  createListPlanAccount,
} from './PlanAccounts.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';

export function usePlanAccounts(
  params?: ListPlanAccountsQuery,
  enabled = true,
) {
  const query = useInfiniteQuery(
    ['planAccounts', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listPlanAccounts(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePlanAccountsAll(
  params?: ListPlanAccountsQuery,
  enabled = true,
) {
  const query = useInfiniteQuery(
    ['planAccountsAll', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listPlanAccountsAll(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled,
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePlanAccount() {
  const mutation = useMutation((id: number) => getPlanAccount(id));

  useServiceError(mutation.error);

  return mutation;
}

export function usePlanAccountDetail(planAccountId?: number) {
  const query = useQuery(
    ['planAccount/detail', planAccountId],
    () => (planAccountId ? getPlanAccount(planAccountId) : undefined),
    { enabled: !!planAccountId },
  );

  useServiceError(query.error);

  return query;
}

export function useCreatePlanAccount() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreatePlanAccountRequest) => createPlanAccount(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'planAccounts.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'planAccounts.create.notification.successDescription',
          }),
        });

        queryClient.invalidateQueries(['planAccounts']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdatePlanAccount() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: UpdatePlanAccountRequest) => updatePlanAccount(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'planAccounts.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'planAccounts.update.notification.successDescription',
          }),
        });

        queryClient.invalidateQueries(['planAccounts']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeletePlanAccount() {
  const intl = useIntl();

  const queryClient = useQueryClient();
  const mutation = useMutation((id: number) => deletePlanAccount(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'planAccounts.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'planAccounts.remove.notification.successDescription',
        }),
      });

      queryClient.invalidateQueries(['planAccounts']);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes(
              'Unable to delete plan account. There are links with the supplier module',
            )
              ? 'Não foi possível excluir o plano de contas. Existem vincúlos com outros módulos.'
              : err.includes(
                  'It is necessary to delete the linked analytical account plans',
                )
              ? 'É necessário excluir os planos de contas analíticos vinculados'
              : err.includes('Plan Account cannot be deleted due to relation.')
              ? 'Não é possivel excluir o plano de contas, pois ele tem vínculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActivateOrInactivatePlanAccount() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (planAccountId: number) => activateOrInactivatePlanAccount(planAccountId),
    {
      onSuccess() {
        message.success(
          intl.formatMessage({
            id: 'planAccounts.status.message.success',
          }),
        );

        queryClient.invalidateQueries(['planAccounts']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function usePlanAccountLast() {
  const mutation = useMutation((params: ListPlanAccountsQuery) =>
    lastPlanAccount(params),
  );

  useServiceError(mutation.error);

  return mutation;
}

export function usePlanAccountCreateList() {
  const queryClient = useQueryClient();

  const mutation = useMutation(createListPlanAccount, {
    async onSuccess() {
      await queryClient.invalidateQueries(['planAccounts']);
    },

    onError(error) {
      notificationError(error);
    },
  });

  return mutation;
}
